import React, { useEffect, useState } from 'react';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { useNavigate, useParams } from 'react-router-dom';
import useDashboardContext from 'contexts/dashboard.context';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import ProcurementRequestItemReceive, {
  receiveProcurementItemsPayload
} from 'components/procurement-request-item-receive';
import { ProcurementItemType } from 'models/procurement.model';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';

const ReceiveItems = () => {
  const router = useNavigate();
  const { setIsBackLink, setBackLinkTitle, activeStore } = useDashboardContext();
  const [loading, setLoading] = useState(false);
  const [receiveLoading, setReceiveLoading] = useState(false);
  const { ids } = useParams();
  const [editedItems, setEditedItems] = useState<receiveProcurementItemsPayload[]>([]);
  const [procurementItems, setProcurementItems] = useState<ProcurementItemType[]>([]);
  const { can } = useRoleContext();
  const canWriteProcurement = can(InventoryModule.Procurement, RolePermission.Write);

  const getProcurementItems = async () => {
    if (!activeStore) return;
    try {
      setLoading(true);
      const idsArray = ids?.includes(',') ? ids.split(',') : [ids || ''];

      const res: { data: { data: ProcurementItemType[] } } = await apiService.post(
        `/procurement/get-items`,
        {
          data: { procurement_item_ids: idsArray },
          version: 'v2'
        }
      );

      if (res) {
        const data = res?.data?.data;

        setProcurementItems(data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getProcurementItems();
  }, [ids]);

  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle('Return to item requests');
    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);

  const handleItemChange = (updatedItem: receiveProcurementItemsPayload) => {
    setEditedItems((prev) => {
      const index = prev.findIndex((item) => item.id === updatedItem.id);
      if (index === -1) {
        return [...prev, updatedItem];
      }
      const next = [...prev];
      next[index] = updatedItem;
      return next;
    });
  };

  const handleUpdate = async () => {
    if (!canWriteProcurement) return;
    const data = {
      note: '',
      items: editedItems.map((item) => ({
        ...item,
        expiration_date: item.expiration_date ? new Date(item.expiration_date).toISOString() : null
      }))
    };
    try {
      setReceiveLoading(true);
      const res = await apiService.put(
        `procurement/${procurementItems[0]?.procurement_request_id}/receive`,
        {
          data,
          version: 'v2'
        }
      );
      if (res) {
        toast(<ToastMessage type='success' message=' Items received' />);
        router(-1);
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    } finally {
      setReceiveLoading(false);
    }
  };
  return (
    <div>
      <div className='border-4 border-[#F7F7F8] rounded py-[24px]  md:max-w-[670px]'>
        <div className='flex justify-between px-[28px]'>
          <div className=' pb-5'>
            <p className='font-semibold text-[24px]'>Update received items </p>
            <p className='text-[#6F7376] text-sm'>
              State the quantity received in good condition where necessary
            </p>
          </div>
        </div>
        {loading ? (
          <div className='flex justify-center items-center h-[400px]'>
            <p className=''>Loading items</p>
          </div>
        ) : !procurementItems.length ? (
          <div className='flex justify-center items-center h-[400px]'>
            <p>No items to receive</p>
          </div>
        ) : (
          <div className='pb-4 space-y-6'>
            {procurementItems.map((item) => (
              <ProcurementRequestItemReceive
                key={item.id}
                item={item}
                onItemChange={handleItemChange}
              />
            ))}
          </div>
        )}
        <div>
          <div className='flex gap-6 items-center justify-end mt-6 mx-[28px] ml-auto'>
            <Button variant='link' color='gray' small onClick={() => router(-1)}>
              Back
            </Button>
            {canWriteProcurement && (
              <Button
                variant='block'
                color='primary'
                small
                className='w-[153px] h-[43px]'
                onClick={handleUpdate}
                loading={receiveLoading}
                disabled={
                  receiveLoading ||
                  !editedItems.length ||
                  editedItems.some(
                    (item) =>
                      !item.price_per_unit || !item.quantity_received || !item.expiration_date
                  )
                }>
                Update
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiveItems;
